import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Users, Search, RefreshCw, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesCustomers() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [subCustomers, setSubCustomers] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, [token]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/sales/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        setSubCustomers(data.sub_customers || []);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c => 
    !search || 
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_person?.toLowerCase().includes(search.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredSubCustomers = subCustomers.filter(c => 
    !search || 
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.contact_person?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customers
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search customers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            <Button variant="outline" onClick={fetchCustomers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-slate-500">Partners and sub-customers will be pulled from Radius (future integration)</p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>City</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.map((c) => (
              <TableRow key={c.customer_id}>
                <TableCell className="font-mono text-sm">{c.customer_code}</TableCell>
                <TableCell className="font-medium">{c.company_name || c.contact_person}</TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p>{c.contact_person}</p>
                    <p className="text-slate-500">{c.contact_phone}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={c.customer_type === 'partner' ? 'default' : 'outline'}>
                    {c.customer_type || 'direct'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{c.gst_number || '-'}</TableCell>
                <TableCell>{c.city}</TableCell>
              </TableRow>
            ))}
            {filteredSubCustomers.map((c) => (
              <TableRow key={c.sub_customer_id} className="bg-slate-50">
                <TableCell className="font-mono text-sm">{c.sub_customer_code}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{c.company_name}</p>
                    <p className="text-xs text-blue-600">↳ {c.parent_partner_name}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p>{c.contact_person}</p>
                    <p className="text-slate-500">{c.contact_phone}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">sub-customer</Badge>
                </TableCell>
                <TableCell className="text-xs">{c.gst_number || '-'}</TableCell>
                <TableCell>{c.city}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredCustomers.length === 0 && filteredSubCustomers.length === 0 && (
          <div className="text-center py-8 text-slate-500">No customers found</div>
        )}
      </CardContent>
    </Card>
  );
}
